/**
 * MFA OTP Send Step
 *
 * Two-phase UI for SMS/Email OTP delivery:
 * 1. "Send Code" button (before sending)
 * 2. Code input + resend countdown (after sending)
 *
 * @module @puffin-core/mfa-ui/internal/MfaOtpSendStep
 */

import PropTypes from 'prop-types';
// The NARROW subpath, never the barrel (see PasskeyManagement): mfa-ui's suites are router-free.
import { InlineFeedback } from '@puffin-core/page-shell/inline-feedback';
import { useOtpSendFlow } from '../hooks/useOtpSendFlow.js';
import { MfaCodeInput } from '../MfaCodeInput.jsx';

/**
 * @param {Object} props
 * @param {string} props.method - OTP method ('sms' or 'email')
 * @param {(method: string) => Promise<void>} props.onSendOtp - OTP send callback
 * @param {string} props.code - Current code value
 * @param {(code: string) => void} props.onCodeChange - Code change handler
 * @param {() => void} props.onSubmit - Submit handler (for auto-submit)
 * @param {boolean} props.autoSubmit - Whether to auto-submit
 * @param {number} props.autoSubmitDelay - Auto-submit debounce in ms
 * @param {boolean} props.isLoading - Whether verification is in progress
 * @param {string|null} props.error - Verification error
 * @param {string} [props.errorId] - Error element ID for aria-describedby
 * @param {number} [props.resendCooldown=60] - Cooldown in seconds
 * @param {Function} props.t - Translation function
 * @returns {React.JSX.Element}
 */
export function MfaOtpSendStep({
  method,
  onSendOtp,
  code,
  onCodeChange,
  onSubmit,
  autoSubmit,
  autoSubmitDelay,
  isLoading,
  error,
  errorId,
  resendCooldown = 60,
  t,
}) {
  const { sent, sending, sendError, sendNotice, secondsRemaining, send, resend } = useOtpSendFlow({
    onSendOtp,
    method,
    cooldown: resendCooldown,
    t,
  });

  if (!sent) {
    return (
      <div data-testid="mfa-otp-send-step">
        <button
          type="button"
          className="btn btn-primary mfa-send-code-btn"
          onClick={send}
          disabled={sending}
          data-testid="mfa-send-code-btn"
        >
          {sending ? t('btn_sending') : t('btn_send')}
        </button>
        {/* SAVE-FEEDBACK-001 (SAVE-FEEDBACK-CANON-001 P3): a failed send is the ONE feedback component —
            `role="alert"` is its, never hand-written `.alert` markup. */}
        <InlineFeedback tone="error" message={sendError} testId="mfa-send-error" />
      </div>
    );
  }

  return (
    <div data-testid="mfa-otp-code-step">
      {sendNotice && <InlineFeedback tone="info" message={sendNotice} testId="mfa-send-notice" />}
      <MfaCodeInput
        code={code}
        onChange={onCodeChange}
        onSubmit={onSubmit}
        label={t('label_code')}
        autoSubmit={autoSubmit}
        autoSubmitDelay={autoSubmitDelay}
        focusOnMount
        isLoading={isLoading}
        error={error}
        errorId={errorId}
      />
      <div className="mfa-resend-row">
        {secondsRemaining > 0 ? (
          <span className="mfa-resend-countdown" data-testid="mfa-resend-countdown">
            {t('btn_resend')} {t('resend_countdown', { seconds: secondsRemaining })}
          </span>
        ) : (
          <button
            type="button"
            className="btn btn-link"
            onClick={resend}
            disabled={sending}
            data-testid="mfa-resend-btn"
          >
            {t('btn_resend')}
          </button>
        )}
      </div>
    </div>
  );
}

MfaOtpSendStep.propTypes = {
  method: PropTypes.string.isRequired,
  onSendOtp: PropTypes.func.isRequired,
  code: PropTypes.string.isRequired,
  onCodeChange: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  autoSubmit: PropTypes.bool.isRequired,
  autoSubmitDelay: PropTypes.number.isRequired,
  isLoading: PropTypes.bool.isRequired,
  error: PropTypes.oneOfType([PropTypes.string, PropTypes.instanceOf(Error)]),
  errorId: PropTypes.string,
  resendCooldown: PropTypes.number,
  t: PropTypes.func.isRequired,
};
